// src/pages/dashboard/Premium/components/PremiumHistory.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PremiumHistoryItem, ToolKey } from '../../api/premiumService';
import { ORDER, icons } from './premiumConstants';
import { ChevronDown, Search as SearchIcon, Check } from 'lucide-react';

interface Props {
  history: PremiumHistoryItem[];
  toolFilter: ToolKey | 'all';
  setToolFilter: (v: ToolKey | 'all') => void;
}

const INPUT_KEYS_ORDER = [
  'domain','url','email','company','ip','tld','query','subdomain','keyword','target','asn','cidr','port'
] as const;
type KnownInputKey = typeof INPUT_KEYS_ORDER[number];

const PAGE_SIZE = 20;

const PremiumHistory: React.FC<Props> = ({ history, toolFilter, setToolFilter }) => {
  const { t } = useTranslation();

  // Dropdown
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!open) return;
      const target = e.target as Node;
      if (popRef.current?.contains(target) || btnRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  // Paginación
  const [page, setPage] = useState(1);

  // Reset página al cambiar filtro o data
  useEffect(() => { setPage(1); }, [toolFilter, history]);

  // Contadores por herramienta
  const toolCounts = useMemo(() => {
    const m = new Map<ToolKey, number>();
    ORDER.forEach(k => m.set(k, 0));
    for (const h of history) m.set(h.tool, (m.get(h.tool) || 0) + 1);
    return m;
  }, [history]);

  // Filtrado por herramienta
  const filtered = useMemo(() => {
    if (toolFilter === 'all') return history ?? [];
    return (history ?? []).filter(h => h.tool === toolFilter);
  }, [history, toolFilter]);

  // Datos paginados
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  const end = start + PAGE_SIZE;
  const pageItems = filtered.slice(start, end);

  const prevDisabled = safePage <= 1;
  const nextDisabled = safePage >= totalPages;

  const formatDateTime = (d: Date) =>
    d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });

  const parseInput = (input: unknown): {chips: {label: string; value: string}[], raw?: string} => {
    if (input == null) return { chips: [] };
    let obj: any = input;

    if (typeof obj === 'string') {
      try { obj = JSON.parse(obj); }
      catch { return { chips: [], raw: String(input) }; }
    }

    if (typeof obj !== 'object' || Array.isArray(obj)) {
      return { chips: [], raw: JSON.stringify(obj) };
    }

    const keys = Object.keys(obj) as string[];
    const ordered = [
      ...INPUT_KEYS_ORDER.filter(k => keys.includes(k)),
      ...keys.filter(k => !INPUT_KEYS_ORDER.includes(k as KnownInputKey))
    ];

    const chips = ordered.map(k => ({
      label: t(`inputs.${k}`),
      value: typeof obj[k] === 'string' ? obj[k] : JSON.stringify(obj[k])
    }));

    return { chips };
  };
  // Opciones del dropdown (con buscar)
  const options = useMemo(() => {
    const term = search.trim().toLowerCase();
    const base: { key: ToolKey; label: string; count: number }[] = ORDER.map(k => ({
      key: k,
      label: t(`tools.${k}`),
      count: toolCounts.get(k) || 0
    }));
    return term ? base.filter(o => o.label.toLowerCase().includes(term)) : base;
  }, [search, toolCounts, t]);

  const currentLabel =
    toolFilter === 'all'
      ? t('premium.all_tools')
      : t(`tools.${toolFilter}`);

  return (
    <section className="mt-12">
      {/* Header + subtítulo */}
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="h-2 w-2 rounded-full bg-indigo-500" />
          <h2 className="text-xl font-extrabold tracking-tight text-gray-900">
            {t('premium.history_title')}
          </h2>
        </div>

        {/* Dropdown */}
        <div className="relative">
          <button
            ref={btnRef}
            onClick={() => setOpen(o => !o)}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <span className="inline-flex h-5 w-5 items-center justify-center">
              {toolFilter === 'all' ? <span className="text-gray-400">∞</span> : icons[toolFilter]}
            </span>
            {currentLabel}
            <span className="rounded-full bg-gray-900/90 px-2 text-[11px] font-semibold text-white">
              {toolFilter === 'all' ? history.length : (toolCounts.get(toolFilter as ToolKey) || 0)}
            </span>
            <ChevronDown className="h-4 w-4 opacity-70" />
          </button>

          {open && (
            <div
              ref={popRef}
              className="absolute right-0 z-20 mt-2 w-80 rounded-xl border border-gray-200 bg-white p-2 shadow-lg"
              role="listbox"
            >
              <div className="mb-2 flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5">
                <SearchIcon className="h-4 w-4 text-gray-500" />
                <input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('premium.search_tool') as string}
                  className="w-full bg-transparent text-sm outline-none"
                />
              </div>

              <button
                onClick={() => { setToolFilter('all'); setOpen(false); setSearch(''); }}
                className={`flex w-full items-center justify-between rounded-lg px-2 py-2 text-left text-sm hover:bg-gray-50 ${
                  toolFilter === 'all' ? 'bg-gray-50' : ''
                }`}
              >
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-5 w-5 items-center justify-center text-gray-400">∞</span>
                  {t('premium.all_tools')}
                </span>
                <span className="ml-2 rounded-full bg-gray-900/90 px-2 text-[11px] font-semibold text-white">
                  {history.length}
                </span>
                {toolFilter === 'all' && <Check className="ml-1 h-4 w-4 text-indigo-600" />}
              </button>

              <div className="mt-1 max-h-64 overflow-auto pr-1">
                {options.map(o => (
                  <button
                    key={o.key}
                    onClick={() => { setToolFilter(o.key); setOpen(false); setSearch(''); }}
                    className={`flex w-full items-center justify-between rounded-lg px-2 py-2 text-left text-sm hover:bg-gray-50 ${
                      toolFilter === o.key ? 'bg-gray-50' : ''
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="inline-flex h-5 w-5 items-center justify-center">{icons[o.key]}</span>
                      {o.label}
                    </span>
                    <span className="ml-2 rounded-full bg-black/10 px-2 text-[11px]">{o.count}</span>
                    {toolFilter === o.key && <Check className="ml-1 h-4 w-4 text-indigo-600" />}
                  </button>
                ))}
                {options.length === 0 && (
                  <div className="px-2 py-2 text-sm text-gray-500">
                    {t('premium.no_results')}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Subtítulo */}
      <p className="mb-6 text-sm text-gray-500">
        {t('premium.history_subtitle')}
      </p>

      {/* Lista (1 por fila) */}
      {pageItems.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center">
          <p className="text-sm text-gray-600">{t('premium.no_history')}</p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {pageItems.map((h) => {
              const parsed = parseInput(h.input);
              const rawStr =
                typeof h.input === 'string'
                  ? h.input
                  : (h.input ? JSON.stringify(h.input) : '');

              return (
                <div key={h.id} className="group relative">
                  <div className="rounded-3xl p-[1px] bg-gradient-to-br from-indigo-200 via-fuchsia-200 to-transparent">
                    <div className="rounded-3xl bg-white/90 backdrop-blur-sm p-5 shadow-sm transition-all duration-200 group-hover:shadow-md group-hover:-translate-y-[1px] border border-gray-200">
                      <div className="flex items-start gap-4">
                        <span className="relative inline-flex h-14 w-14 shrink-0 items-center justify-center">
                          <span className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-indigo-100 to-fuchsia-100 blur-sm opacity-90" />
                          <span className="relative inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white ring-1 ring-gray-100">
                            {icons[h.tool]}
                          </span>
                        </span>

                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <h3 className="truncate text-base font-semibold text-gray-900">
                              {t(`tools.${h.tool}`)}
                            </h3>
                            <div className="flex items-center gap-2">
                              <span className="shrink-0 rounded-full bg-gray-900/90 px-3 py-1 text-[11px] font-semibold text-white">
                                {formatDateTime(new Date(h.created_at))}
                              </span>
                            </div>
                          </div>

                          {/* chips del input */}
                          {parsed.chips.length > 0 ? (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {parsed.chips.map((c, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex max-w-full items-center gap-2 truncate rounded-xl bg-gray-50 px-3 py-1 text-xs text-gray-800 ring-1 ring-gray-200"
                                  title={`${c.label}: ${c.value}`}
                                >
                                  <span className="text-gray-500">{c.label}:</span>
                                  <span className="font-mono truncate">{c.value}</span>
                                </span>
                              ))}
                            </div>
                          ) : rawStr ? (
                            <div className="mt-3 rounded-xl bg-gray-50 ring-1 ring-gray-200 p-3">
                              <code className="block max-w-full truncate text-[12px] text-gray-700">
                                {rawStr}
                              </code>
                            </div>
                          ) : (
                            <div className="mt-3 text-xs text-gray-400">—</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-fuchsia-200 opacity-0 blur-3xl transition group-hover:opacity-70" />
                </div>
              );
            })}
          </div>

          {/* Controles de paginación */}
          <div className="mt-6 flex items-center justify-between gap-3">
            <button
              disabled={prevDisabled}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              className={`rounded-xl px-4 py-2 text-sm font-medium ring-1 transition ${
                prevDisabled
                  ? 'cursor-not-allowed bg-white text-gray-400 ring-gray-200'
                  : 'bg-white text-gray-700 ring-gray-200 hover:bg-gray-50'
              }`}
            >
              {t('premium.prev')}
            </button>

            <div className="text-sm text-gray-600">
              {t('premium.page_of', { page: safePage, total: totalPages })}
            </div>

            <button
              disabled={nextDisabled}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              className={`rounded-xl px-4 py-2 text-sm font-medium ring-1 transition ${
                nextDisabled
                  ? 'cursor-not-allowed bg-white text-gray-400 ring-gray-200'
                  : 'bg-white text-gray-700 ring-gray-200 hover:bg-gray-50'
              }`}
            >
              {t('premium.next')}
            </button>
          </div>
        </>
      )}
    </section>
  );
};

export default PremiumHistory;
